// Cola offline de capturas de muestreo (IndexedDB nativo, sin dependencias).
//
// El muestreador captura en el campo SIN señal: el POST se encola aquí y se
// sincroniza solo cuando vuelve la conexión. Cada item lleva client_uuid para
// que el backend pueda deduplicar (upsert idempotente).

const DB_NAME = 'mya-aplicaciones-offline';
const DB_VERSION = 1;
const STORE = 'capturas_pendientes';

export interface CapturaPendiente {
  client_uuid: string;          // id único del item (clave primaria)
  muestreoId: number;
  empresaId: number;
  labor_nombre: string;
  payload: any;                 // body del POST a /captura
  creado_at: string;            // ISO local de cuándo se capturó
  intentos: number;             // reintentos de sync
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'client_uuid' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDB().then(db => new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    t.oncomplete = () => db.close();
  }));
}

export function uuid(): string {
  return (crypto as any).randomUUID
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });
}

export async function encolar(item: Omit<CapturaPendiente, 'intentos'>): Promise<void> {
  await tx('readwrite', (s) => s.put({ ...item, intentos: 0 }));
}

export async function listarPendientes(): Promise<CapturaPendiente[]> {
  const all = await tx<CapturaPendiente[]>('readonly', (s) => s.getAll() as any);
  return (all || []).sort((a, b) => a.creado_at.localeCompare(b.creado_at));
}

export async function contarPendientes(): Promise<number> {
  return tx<number>('readonly', (s) => s.count() as any);
}

export async function quitar(client_uuid: string): Promise<void> {
  await tx('readwrite', (s) => s.delete(client_uuid));
}

export async function marcarIntento(client_uuid: string): Promise<void> {
  const item = await tx<CapturaPendiente>('readonly', (s) => s.get(client_uuid) as any);
  if (item) await tx('readwrite', (s) => s.put({ ...item, intentos: (item.intentos || 0) + 1 }));
}
